import * as React from "react";
import {MassiveActionsProcessHeader} from "../components/UtilsComponents";
import {VeboxOverlay} from "../../../components/overlay/VeboxOverlay";
import {CodesValidation} from "../components/CodesValidation";
import {ProcessPage, ProcessStageOrder} from "../../Processes";
import {VeboxForm} from "../../../components/form/VeboxForm";
import {ChooseOperationAndReason} from "./ChooseOperationAndReason";
import {MassiveActionProcessState, MassiveActionsContext} from "../MassiveActionsProvider";
import {CodesCheckAndApprove} from "../components/CodesCheckAndApprove";
import {SelectInputOption} from "@vebox/etui-core";
import {
    AvailableMassivePatch,
    getMassivePatch,
    JSONAvailableMassivePatch,
    lockUnlockReasonsEnum,
    MassiveActions,
    prepareInfoModalItems,
    prepareMassiveAction
} from "../MassiveActionsTypesAndFunctions";
import {useLocation} from "react-router-dom";
import {Display} from "../../../components/Display";
import {LockUnlockEquipmentCompressorContext} from "./LockUnlockEquipmentCompressorProvider";
import {DATA_STATE} from "../../../users/contexts/UsersDashboardContext";
import {Loader} from "../../../components/Utils/loader/Loader";
import {ValidatedEquipmentInfoModal} from "../components/ValidatedEquipmentInfoModal";
import {AppTranslations} from "../../../components/Utils/translations/AppTranslations";
import {MassiveOnSubmitModal} from "../components/MassiveOnSubmitModal";
import {DefaultErrorBoundaryFallback} from "../../../ErrorBoundary/DefaultErrorBoundaryFallback";

declare var AppTranslations: AppTranslations;

export const LockUnlockEquipmentCompressor: React.FC<any> = () => {

    const translations = AppTranslations.massiveActions.lockUnlockEquipmentCompressor;

    const {
        validateCodes,
        validatedEquipmentsInfo,
        availableMassivePatchesInfo,
        executeMassiveAction,
    } = React.useContext(MassiveActionsContext);

    const location = useLocation();
    const stepsTitles = [translations.chooseStatusAndReasonTitle, translations.insertEquipmentCodeListTitle, translations.checkAndConfirmTitle];
    const [values, setValues] = React.useState(undefined);
    const {lockUnlockReasons} = React.useContext(LockUnlockEquipmentCompressorContext);
    const [massiveActionProcessState, setMassiveActionProcessState] = React.useState(
        MassiveActionProcessState.INITIAL);
    const [currentMassiveAction, setCurrentMassiveAction] = React.useState<AvailableMassivePatch>();

    const closeModal = () => overlayController.clear()
    const overlayController = VeboxOverlay.useTarget(
        <ValidatedEquipmentInfoModal
            onClose={closeModal}
            title={translations.modalInfoTitle}
            statusElements={prepareInfoModalItems({
                title: translations.validatedEquipmentFoundTitle,
                description: translations.validatedEquipmentFoundDescription
            }, {
                title: translations.validatedEquipmentNotFoundTitle,
                description: translations.validatedEquipmentNotFoundDescription
            })}
        />
    );
    const executeAction = (validatedEquipmentGuids: string[]) => {
        setMassiveActionProcessState(MassiveActionProcessState.PENDING);
        const lockeEquipmentCompressor = values as {
            changeCompressorStatus: { status: SelectInputOption, reason: SelectInputOption }
        }
        if (lockeEquipmentCompressor !== undefined) {
            const {status, reason} = lockeEquipmentCompressor.changeCompressorStatus
            const lockOperation = status.value === lockUnlockReasonsEnum.LOCK_REASONS ? lockUnlockReasonsEnum.LOCK_REASONS : lockUnlockReasonsEnum.UNLOCK_REASONS
            const JSONPatches: JSONAvailableMassivePatch[] = prepareMassiveAction(
                validatedEquipmentGuids,
                availableMassivePatchesInfo.availableMassivePatches,
                {
                    valueInfo: {
                        massiveAction: MassiveActions.LOCK_UNLOCK_EQUIPMENT_COMPRESSOR,
                        lockReasonGuid: reason.value,
                        lockOperation
                    }
                })
            executeMassiveAction(JSONPatches)
                .then(() => {
                    overlayController.show(
                        <MassiveOnSubmitModal
                            title={translations.checkAndConfirmSuccessTitle}
                            descriptions={[translations.checkAndConfirmSuccessDescription]}
                            onClose={closeModal}
                            success={true}
                        />
                    )
                    setMassiveActionProcessState(MassiveActionProcessState.INITIAL);
                })
                .catch(manageErrors)
        }
    }

    const manageErrors = (error: { errorType: string, message: string }) => {
        let descriptions: React.ReactNode[] = []
        switch (error.errorType) {
            case 'UNABLE_TO_DELETE_FILE_ON_SFTP':
            case 'UNABLE_TO_UPLOAD_FILE_ON_SFTP':
                descriptions.push(<DefaultErrorBoundaryFallback
                    errorMessage={AppTranslations.errorMessages.unableToPerformTheOperation}
                    style={{backgroundColor: "white"}}
                />)
                break;
            default:
                descriptions.push(<span>{translations.checkAndConfirmErrorDescription}</span>)
                break;
        }
        overlayController.show(
            <MassiveOnSubmitModal
                title={translations.checkAndConfirmErrorTitle}
                descriptions={descriptions}
                onClose={closeModal}
            />)
        setMassiveActionProcessState(MassiveActionProcessState.FAILURE);
    }

    React.useEffect(() => {
        if (availableMassivePatchesInfo.availableMassiveState === DATA_STATE.loaded) {
            setCurrentMassiveAction(
                getMassivePatch(
                    MassiveActions.LOCK_UNLOCK_EQUIPMENT_COMPRESSOR,
                    availableMassivePatchesInfo.availableMassivePatches))
        }
    }, [availableMassivePatchesInfo.availableMassiveState])

    return (
        <>
            <ProcessPage total={3} className={"flx-col"}>
                <MassiveActionsProcessHeader
                    processHeaderTitle={translations.lockUnlockEquipmentCompressorTitle}
                    processIndicatorsTitles={stepsTitles}
                />
                <VeboxOverlay.Target {...overlayController.targetProps} className={"process-stages flx-row"}>
                    <VeboxForm onChange={setValues} values={values}>
                        <Display isVisible={lockUnlockReasons && lockUnlockReasons.reasonsState === DATA_STATE.loaded}>
                            <ChooseOperationAndReason
                                title={translations.chooseStatusAndReasonTitle}
                                cardTitle={translations.chooseStatusAndReasonCardTitle}
                                stepNumber={1}
                                stageOrder={ProcessStageOrder.FIRST}
                                operationDescription={translations.lockUnlockEquipmentCompressorDescription}
                                operationTitle={translations.lockUnlockEquipmentCompressorTitle}
                                massiveActionProcessState={massiveActionProcessState}
                            />
                            <CodesValidation
                                validationFunction={validateCodes}
                                stepNumber={2}
                                stageOrder={ProcessStageOrder.MIDDLE}
                                title={translations.insertEquipmentCodeListTitle}
                                cardTitle={translations.codeValidationCardTitle}
                                errorMessage={translations.codeValidationErrorTitle}
                                errorDescription={translations.codeValidationErrorDescription}
                                massiveActionProcessState={massiveActionProcessState}
                            />
                            <CodesCheckAndApprove
                                overlayController={overlayController}
                                stepNumber={3}
                                stageOrder={ProcessStageOrder.LAST}
                                title={translations.checkAndConfirmTitle}
                                submitFunction={executeAction}
                                validatedEquipmentsInfo={validatedEquipmentsInfo}
                                massiveActionProcessState={massiveActionProcessState}
                            />
                        </Display>
                        <Display isVisible={lockUnlockReasons === undefined || lockUnlockReasons.reasonsState === DATA_STATE.isLoading}>
                            <Loader/>
                        </Display>
                    </VeboxForm>
                </VeboxOverlay.Target>
            </ProcessPage>
        </>

    )
}

